import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/auth/hooks';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

type TokensView = {
  business_id: string;
  tokens_used: number;
  display_used: number;
  tokens_limit: number;
  display_limit: number;
  period_started: string;
  period_ends: string;
  remaining: number;
  display_remaining: number;
};

export default function MCPAgentChat() {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState<TokensView | null>(null);

  const businessId = user?.user_metadata?.business_id as string | undefined;

  async function loadTokens(bizId: string) {
    const { data, error } = await supabase.rpc('get_remaining_tokens', {
      p_business_id: bizId
    });
    if (!error && data && data.available) {
      setTokens({
        tokens_limit: data.tokens_limit,
        tokens_used: data.tokens_used,
        remaining: data.tokens_remaining,
        display_limit: data.tokens_limit,
        display_used: data.tokens_used,
        display_remaining: data.tokens_remaining,
      } as TokensView);
    }
  }

  useEffect(() => {
    if (businessId) loadTokens(businessId);
  }, [businessId]);

  const handleSend = async () => {
    if (!message) return;
    if (!businessId) {
      setResponse({ error: 'לא זוהה עסק. נסה להתחבר מחדש.' });
      return;
    }
    if (tokens && tokens.remaining <= 0) {
      setResponse({ error: 'נגמרו הטוקנים בחבילה. שדרג/י כדי להמשיך.' });
      return;
    }

    setLoading(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      // בקשה ראשונה ל־MCP Agent
      let res = await fetch('https://nkuqcyelxgyihrxyvitb.functions.supabase.co/mcp-agent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token ?? ''}`,
        },
        body: JSON.stringify({
          businessId,
          userId: user?.id,  // ← הוסף
          message,
        }),
      });

      let data = await res.json();

      // אם צריך אישור מחיקה – אשר ושלח שוב עם confirm + sql
      if (data.requireConfirmation) {
        const ok = window.confirm(data.warning || 'האם לאשר מחיקה?');
        if (ok) {
          // הגנה: ודא שיש SQL לחתימה
          if (!data.sql) {
            setResponse({ error: 'לא נמצא SQL לאישור. נסה שוב.' });
            setLoading(false);
            return;
          }
          res = await fetch('https://nkuqcyelxgyihrxyvitb.functions.supabase.co/mcp-agent', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token ?? ''}`,
            },
            body: JSON.stringify({
              businessId,
              userId: user?.id, // ← הוסף
              message,
              confirm: true,
              sql: data.sql,    // ← חשוב
            }),
          });
          data = await res.json();
        } else {
          setLoading(false);
          return;
        }
      }

      setResponse(data);

      // רענון סטטוס טוקנים לאחר שהשרת ספר לפי completion.usage
      await loadTokens(businessId);
    } catch (err: any) {
      setResponse({ error: 'שגיאה בשליחה: ' + (err?.message ?? String(err)) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">MCP Agent</h2>

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="כתוב שאלה..."
        className="w-full border rounded p-2"
        rows={4}
      />

      <button
        onClick={handleSend}
        className="mt-2 bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
        disabled={loading}
      >
        {loading ? 'שולח...' : 'שלח'}
      </button>

      {tokens && (
        <div className="mt-3 text-sm text-right">
          נשארו {tokens.remaining.toLocaleString()} / {tokens.tokens_limit.toLocaleString()} טוקנים
          <div className="w-full h-2 bg-gray-200 rounded mt-1">
            <div
              className="h-2 bg-blue-600 rounded"
              style={{
                width: `${Math.min(
                  100,
                  100 * (tokens.tokens_limit - tokens.remaining) / Math.max(1, tokens.tokens_limit)
                )}%`,
              }}
            />
          </div>
        </div>
      )}

      {response && (
        <div className="mt-4 bg-gray-100 border rounded p-3 space-y-2">
          <strong>📤 תוצאה:</strong>

          {response.error && <p className="text-red-600">{response.error}</p>}

          {response.data && Array.isArray(response.data) ? (
            <div className="whitespace-pre-line text-right">
              {response.data.map((item: any, index: number) => {
                const time = item.start_time
                  ? format(new Date(item.start_time), 'HH:mm', { locale: he })
                  : 'שעה לא זמינה';
                const name = item.customer_name || item.name || 'לקוח לא מזוהה';
                return (
                  <div key={index}>
                    • {time} - {name}
                  </div>
                );
              })}
            </div>
          ) : (
            response.reply && (
              <p className="text-right text-gray-800 font-semibold whitespace-pre-line">
                {response.reply}
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
}
